import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  useMediaQuery,
  Checkbox,
  FormControlLabel,
  useTheme,
  Button,
  CircularProgress,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { Images } from "../../utils/Images";
import AuthenticationTextInput from "../../components/common/TextInput/AuthenticationTextInput";
import { DonateButtonVariant } from "../../components/common/Button/Button";
import DonateLeftBanner from "../../components/common/DonateLeftBanner";
import { styled } from "@mui/material/styles";
import BannerSection from "../../components/common/BannerSection";
import CurrencySelectorPopup from "../../components/common/CurrencySelectorPopup"; // Import the popup
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { handleDonate } from "../../utils/Donation";
import { useSelector, useDispatch } from "react-redux";
import { validateDonationAmount } from "../../utils/donationValidation";
import {
  sanitizeFormFieldValue,
  validateIndianMobile,
} from "../../utils/phoneValidation";
import { fetchCmsHomeAction } from "../../store/slices/cmsSlice";

const supportOptions = [
  { label: "12% - ₹360", value: "12" },
  { label: "15% - ₹450", value: "15" },
  { label: "20% - ₹600", value: "20" },
];

const currencyList = [
  { code: "INR", symbol: "₹" },
  { code: "USD", symbol: "$" },
  { code: "AED", symbol: "د.إ" },
  { code: "CAD", symbol: "C$" },
  { code: "AUD", symbol: "A$" },
  { code: "GBP", symbol: "£" },
];

const DonateMonthlyPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { user } = useSelector((state) => state.auth);
  const cmsHome = useSelector((state) => state.cms.home);
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Fetch CMS data if not available
  useEffect(() => {
    if (!cmsHome) {
      dispatch(fetchCmsHomeAction());
    }
  }, [cmsHome, dispatch]);
  
  const theme = useTheme();
  // Custom media query for screens 900px to 1200px
  const isMediumScreen = useMediaQuery(
    "(min-width: 900px) and (max-width: 1200px)"
  );
  // Custom media query for screens below 900px
  const isSmallScreen = useMediaQuery("(max-width: 899px)");
  // Custom media query for mobile screens (600px and below)
  const isMobile = useMediaQuery("(max-width: 600px)");
  const isAbove1200 = useMediaQuery("(min-width: 1200px)");
  const mobileView = isMobile;
  
  // Get selected monthly donation data from URL params
  const selectedMonthlyDonationId = searchParams.get('id');
  const selectedMonthlyDonationTitle = searchParams.get('title');
  const selectedAmountFromParams = searchParams.get('amount');
  
  const [showCurrencyPopup, setShowCurrencyPopup] = useState(false);
  // DonateRightForm state and handlers
  const [supportBy, setSupportBy] = useState("12");
  const [amount, setAmount] = useState(selectedAmountFromParams || "1000");
  const [currency, setCurrency] = useState(currencyList[0]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    postal: "",
  });
  const [whatsapp, setWhatsapp] = useState(true);
  const [amountError, setAmountError] = useState(""); // Validation error for amount
  
  // Auto-fill form when user is logged in
  useEffect(() => {
    if (user && user.id) {
      // Only auto-fill if fields are empty and user has the data
      setForm((prevForm) => ({
        ...prevForm,
        name: prevForm.name || user.name || "",
        email: prevForm.email || user.email || "",
        phone: prevForm.phone || user.phone || user.mobile || "",
        // postal code is not available in user profile, so leave it empty
      }));
    }
  }, [user]);
  
  // Update amount when URL params change
  useEffect(() => {
    if (selectedAmountFromParams) {
      setAmount(selectedAmountFromParams);
    } else {
      // Clear amount if no amount is passed in params
      setAmount("");
    }
  }, [selectedAmountFromParams]);

  const handleInputChange = (field) => (e) => {
    const value = sanitizeFormFieldValue(field, e.target.value);
    setForm({ ...form, [field]: value });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: "" });
    }
  };

  const validateForm = () => {
    const validationErrors = {};
    if (!form.name) validationErrors.name = "Full Name is required";
    if (!form.email) validationErrors.email = "Email is required";
    const phoneCheck = validateIndianMobile(form.phone);
    if (!phoneCheck.isValid) validationErrors.phone = phoneCheck.error;
    if (!form.postal) validationErrors.postal = "Postal Code is required";
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return false;
    }
    return true;
  };

  const handleDonateClick = async () => {
    if (!validateForm()) {
      return;
    }
    
    // Validate minimum donation amount (check if overseas based on currency)
    const isOverseas = currency.code !== "INR";
    const validation = validateDonationAmount(amount, cmsHome, isOverseas, currency.symbol);
    if (!validation.isValid) {
      setAmountError(validation.error);
      return;
    }
    
    // Clear error if validation passes
    setAmountError("");

    if (loading) return;

    setLoading(true);
    try {
      const additionalData = {
        currency: currency.code,
        postalCode: form.postal,
        whatsappUpdates: whatsapp,
        campaignId: selectedMonthlyDonationId || "",
        campaignName: selectedMonthlyDonationTitle || "",
        donationType: "monthly", // Mark as monthly donation
      };

      await handleDonate(
        form,
        Number(amount),
        navigate,
        additionalData,
        user?.id,
        null // No snackbar for now
      );
    } catch (error) {
      console.error("Donation error:", error);
      alert("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Currency dropdown handlers
 const handleCurrencyClick = (event) => {
    if (mobileView) {
      setShowCurrencyPopup(true);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };
  const handleCurrencySelect = (cur) => {
    setCurrency(cur);
    setAnchorEl(null);
  };
  
  const handleCurrencyPopupSelect = (cur) => {
    setCurrency(cur);
  };

  const handleCloseCurrencyPopup = () => {
    setShowCurrencyPopup(false);
  };
  const CustomDonateButton = styled(Button)(
    ({ theme, isMobile, isTablet }) => ({
      backgroundColor: "#F04951",
      color: "#FFFFFF",
      borderRadius: "10px",
      boxShadow: "0px 4px 0px #141414",
      // padding: isMobile ? "12px 16px" : isTablet ? "14px 20px" : "15px 24px",
      width: "100%", // 100% width for responsiveness
      height: isMobile ? "44px" : isTablet ? "48px" : "52px",
      fontFamily: "Red Hat Display, sans-serif",
      fontWeight: 700,
      fontSize: isMobile ? "13px" : isTablet ? "14px" : "15px",
      lineHeight: isMobile ? "13px" : isTablet ? "14px" : "15px",
      textAlign: "center",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: isMobile ? "6px" : isTablet ? "7px" : "8px",
      textTransform: "none",
      transition: "all 0.3s ease",
      "&:hover": {
        backgroundColor: "#d63f46",
        boxShadow: "0px 4px 0px #141414",
      },
      "&:active": {
        boxShadow: "0px 2px 0px #141414",
        transform: "translateY(2px)",
      },
    })
  );
  return (
    <Box>
      {/* Header Section */}
      <Box
        sx={{
          mb: "40px",
          mt: "30px",
          display: isSmallScreen ? "block" : "none",
        }}
      >
        <BannerSection title="Donate Monthly" subtitle={false} />
      </Box>

      <Box
        sx={{
          background: isMobile ? "unset" : "rgba(232, 232, 232, 0.40)",
          p: isMobile ? "0" : isMediumScreen ? "16px" : "30px",
          width: "94.4%",
          maxWidth: "1440px",
          borderRadius: "20px",
          mx: "auto",
        }}
      >
        <Box sx={{ display: "flex", gap: isSmallScreen ? "0" : "24px" }}>
          {/* DonateLeftBanner */}
          <Box>
            <DonateLeftBanner />
          </Box>
          {/* DonateRightForm content */}
          <Box
            sx={{
              background: "#fff",
              borderRadius: isMobile ? "20px" : "20px",
              boxShadow: isMobile ? "none" : "0 4px 24px rgba(0,0,0,0.08)",
              p: isMobile ? "0px" : "30px 26px",
              pb: isSmallScreen ? "80px" : undefined, // breathing room for fixed donate bar on mobile
              minHeight: isMobile ? "auto" : "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              flex: 1,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: isMobile
                    ? "12px"
                    : isMediumScreen
                    ? "14px"
                    : "18px",
                  color: "#1D1D1B",
                  fontWeight: 800,
                  display: isMobile ? "none" : "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: "16px",
                }}
              >
                <Box component="span">
                  <img
                    src={Images.streamlineSharpStar}
                    alt="Orange Sparks"
                    style={{
                      height: "auto",
                      zIndex: 1,
                    }}
                  />
                </Box>
                Donate Monthly
              </Typography>
            </Box>

            {/* Selected Monthly Donation Title */}
            {selectedMonthlyDonationTitle && (
              <Box
                sx={{
                  mb: 2,
                  pb: 2,
                  borderBottom: "1px solid #E8E8E8",
                }}
              >
                <Typography
                  sx={{
                    fontSize: isMobile ? "16px" : "18px",
                    fontWeight: 700,
                    color: "#1D1D1B",
                  }}
                >
                  {selectedMonthlyDonationTitle}
                </Typography>
                {/* Hidden input to store the ID */}
                <input
                  type="hidden"
                  name="monthlyDonationId"
                  value={selectedMonthlyDonationId || ""}
                />
              </Box>
            )}

            {/* Currency + Amount compact input */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                background: "#F5F8ED",
                borderRadius: "8px",
                border: "1.114px  solid #BDD188",
                px: 2,
                py: 1,
                minWidth: isMobile ? "120px" : "180px",
                fontWeight: 600,
                fontSize: isMobile ? "15px" : "18px",
                gap: 1,
                mb: 3,
              }}
            >
              {/* Currency display (read-only - set from previous page) */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  fontWeight: 500,
                  fontSize: isMobile ? "12px" : "18px",
                  color: "#4A4A49",
                  cursor: "default",
                  minWidth: "55px",
                }}
              >
                <Box component="span" sx={{ color: "#4A4A49" }}>
                  {currency.symbol}
                </Box>
                <Typography
                  component="span"
                  sx={{
                    ml: 1,
                    color: "#4A4A49",
                    fontWeight: 700,
                    fontSize: isMobile ? "12px" : "17px",
                    lineHeight: isMobile ? "12px" : "17px",
                  }}
                >
                  {currency.code}
                </Typography>
              </Box>
              <Box sx={{ fontWeight: 600, color: "#222", flex: 1 }}>
                <input
                  type="text"
                  value={amount}
                  readOnly
                  style={{
                    border: "none",
                    background: "transparent",
                    fontWeight: 800,
                    fontSize: isMobile ? "12px" : isMobile ? "14px" : "17px",
                    outline: "none",
                    width: "100%",
                    color: "#4A4A49",
                    lineHeight: isMobile ? "14px" : "17px",
                    borderLeft: "0.835px solid #4a4a494d",
                    paddingLeft: "11px",
                    cursor: "default",
                  }}
                />
              </Box>
            </Box>
            {/* Validation error text */}
            {amountError && (
              <Typography
                sx={{
                  color: "#F04951",
                  fontSize: isMobile ? "11px" : "12px",
                  mt: -2,
                  mb: 2,
                  ml: 1,
                }}
              >
                {amountError}
              </Typography>
            )}
            {/* Form fields */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: isMobile ? "auto" : "auto auto",
                columnGap: "27px",
              }}
            >
              <Box>
                <AuthenticationTextInput
                  label="Full Name"
                  value={form.name}
                  onChange={handleInputChange("name")}
                  placeholder="Enter Full Name"
                  required
                  error={!!errors.name}
                  helperText={errors.name}
                />
              </Box>
              <Box>
                <AuthenticationTextInput
                  label="Email ID"
                  value={form.email}
                  onChange={handleInputChange("email")}
                  placeholder="Enter E mail Id"
                  required
                  error={!!errors.email}
                  helperText={errors.email}
                />
              </Box>
              <Box>
                <AuthenticationTextInput
                  label="Phone Number"
                  value={form.phone}
                  onChange={handleInputChange("phone")}
                  phoneInput
                  placeholder="Enter Phone Number"
                  required
                  error={!!errors.phone}
                  helperText={errors.phone}
                />
              </Box>
              <Box>
                <AuthenticationTextInput
                  label="Postal Code"
                  value={form.postal}
                  onChange={handleInputChange("postal")}
                  placeholder="Enter Postal Code"
                  required
                  error={!!errors.postal}
                  helperText={errors.postal}
                />
              </Box>
            </Box>
            {/* Checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={whatsapp}
                  onChange={(e) => setWhatsapp(e.target.checked)}
                  sx={{
                    color: "#f04951",
                    "&.Mui-checked": { color: "#f04951" },
                    p: 0,
                    mr: "10px",
                    width: isMobile ? "16px" : "20px",
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontSize: isMobile ? "12px" : "14px",
                    fontWeight: 500,
                    color: "#1D1D1B",
                  }}
                >
                  I want to receive transaction and donation updates on WhatsApp
                </Typography>
              }
              sx={{ mb: isMobile ? 1 : "14px", mx: "0" }}
            />
            {/* Terms */}
            <Typography
              sx={{
                fontSize: isMobile ? "10px" : "14px",
                color: "#1D1D1B",
                mb: isMobile ? 2 : 3,
                fontWeight: 500,
              }}
            >
              By continuing, you are agreeing to{" "}
              <Box
                component={Link}
                to="/terms-conditions"
                sx={{ 
                  color: "#FF5858", 
                  cursor: "pointer",
                  textDecoration: "none",
                  "&:hover": {
                    color: "#FF5858",
                    textDecoration: "none"
                  },
                  "&:active": {
                    color: "#FF5858",
                    textDecoration: "none"
                  },
                  "&:visited": {
                    color: "#FF5858",
                    textDecoration: "none"
                  },
                  "&:focus": {
                    color: "#FF5858",
                    textDecoration: "none"
                  }
                }}
              >
                Terms of Use
              </Box>{" "}
              and{" "}
              <Box
                component={Link}
                to="/privacy-policy"
                sx={{ 
                  color: "#FF5858", 
                  cursor: "pointer",
                  textDecoration: "none",
                  "&:hover": {
                    color: "#FF5858",
                    textDecoration: "none"
                  },
                  "&:active": {
                    color: "#FF5858",
                    textDecoration: "none"
                  },
                  "&:visited": {
                    color: "#FF5858",
                    textDecoration: "none"
                  },
                  "&:focus": {
                    color: "#FF5858",
                    textDecoration: "none"
                  }
                }}
              >
                Privacy Policy
              </Box>
            </Typography>
            {/* Donate Button */}
            <div
              style={{
                width: "100%",
                display: isSmallScreen ? "none" : "block",
              }}
            >
              <CustomDonateButton 
                onClick={handleDonateClick} 
                isMobile={isMobile}
                disabled={loading}
                sx={{
                  "&:disabled": {
                    backgroundColor: "#d63f46",
                    opacity: 0.7,
                    cursor: "not-allowed",
                  },
                }}
              >
                {loading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={16} sx={{ color: "#FFFFFF" }} />
                    Processing...
                  </Box>
                ) : (
                  `Donate ${currency.symbol}${amount}`
                )}
              </CustomDonateButton>
            </div>
          </Box>
        </Box>
      </Box>
      <div
        style={{
          width: "100%",
          position: "fixed",
          backgroundColor: "#fff",
          bottom: 0,
          left: 0,
          padding: "16px 10px",
          boxShadow: " 0 -4px 16px 0 rgba(0, 0, 0, 0.10)",
          display: isSmallScreen ? "block" : "none",
        }}
      >
        <CustomDonateButton 
          onClick={handleDonateClick} 
          isMobile={isMobile}
          disabled={loading}
          sx={{
            "&:disabled": {
              backgroundColor: "#d63f46",
              opacity: 0.7,
              cursor: "not-allowed",
            },
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} sx={{ color: "#FFFFFF" }} />
              Processing...
            </Box>
          ) : (
            `Donate ${currency.symbol}${amount}`
          )}
        </CustomDonateButton>
      </div>
       {/* Add Currency Selector Popup */}
      <CurrencySelectorPopup
        open={showCurrencyPopup}
        onClose={handleCloseCurrencyPopup}
        selectedCurrency={currency}
        onCurrencySelect={handleCurrencyPopupSelect}
      />
    </Box>
  );
};

export default DonateMonthlyPage;
